import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from '../../../hooks/translations';
import { useMinimalSearchLazyQuery, useSearchLazyQuery } from './useSearchLazyQuery';

export function useCourseDetailsQuery( courseId, options ) {

    const { locale } = useTranslations();

    // Query for the details for a single curriculum.
    const [ searchQuery, { data: rawData, loading, error, enrollments: enrollmentsData, pageData, refetch  } ] = useSearchLazyQuery();
    const [ minSearchQuery, { data: minData, loading: minLoading } ] = useMinimalSearchLazyQuery( { useSegmentation: false } );
    const [ customError, setCustomError ] = useState( null );

    async function fetch() {
        await searchQuery( {
            types: [ 'courses' ],
            ids: [ courseId ],
            query: '',
            page: 1,
            limit: 1, 
        } );
    }

    async function fetchAnew( courseId ) {
        await searchQuery( {
            types: [ 'courses' ],
            ids: [ courseId ],
            query: '',
            page: 1,
            limit: 1, 
        } );
    }

    async function minFetch() {

        await minSearchQuery( {
            types: [ 'courses' ],
            ids: [ courseId ],
            query: '',
            page: 1,
            limit: 1, 
        } );
    }

    const [ refinedData, setRefinedData ] = useState( null );

    // initial fetch
    useEffect( () => {
        fetch();
    }, [ locale ] );

    // after fetch: set data or do minfetch
    useEffect( () => {
        if ( rawData == null || !enrollmentsData ) {return;}

        if ( pageData.totalResults === 0 ) {
            // user cant see this course, so load info in background
            minFetch();
        } else if ( rawData.length > 0 ) {
            const course = rawData[ 0 ];
            if ( course.prerequisiteData?.locked ) {
                course.learningObject.blockClick = true;
            }
            setRefinedData( course );
        }


    }, [ rawData, enrollmentsData ] );

    // after minfetch: show error and possible set data
    useEffect( () => {
        
        if ( minData == null ) {return;} // if the normal search hasnt even occured, dont do minstuff

        if ( minData.length > 0 ) {
            // this course exists but you cant see it
            setCustomError( {
                titleKey: 'error.noPermission.title',
                descriptionKey: 'error.noPermission.description',
            } );
            setRefinedData( minData[ 0 ] );
        } else {
            // this course does not exist
            setCustomError( {
                titleKey: 'error.course.DNE.title',
                descriptionKey: 'error.course.DNE.description',
            } );
        }
    }, [ minData ] );


    return useMemo ( () => {
        return {
            error: customError ?? error,
            loading: loading || minLoading,
            data: refinedData,
            refetch,
            fetchAnew,
        };
    }, [ refinedData, loading, error, customError, minLoading ] );
}
